import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Snackbar,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Pagination,
  Grid,
  Paper,
  Stack,
  Collapse,
} from "@mui/material";
import {
  Edit,
  Delete,
  ContentCopy,
  PictureAsPdf,
  Visibility,
} from "@mui/icons-material";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";
import EditFilter from "./EditFilter";
import SearchStock from "./SearchStock";
import { useNavigate } from "react-router-dom";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";
const FRONTEND_URL =
  process.env.REACT_APP_FRONT_BASE_URL || "http://localhost:3001";
const IMAGE_BASE_URL = "https://kumuduorderapp.blob.core.windows.net/804-jjj/";

const FilterForm = () => {
  const navigate = useNavigate(); // ✅ Use navigation hook

  const selectedManufacturer = JSON.parse(localStorage.getItem("selectedMfr"));
  const mfrId = selectedManufacturer?.id; // Safely access the ID

  const isLoggedInUser = JSON.parse(localStorage.getItem("user"));

  const [view, setView] = useState("get");
  const [filterName, setFilterName] = useState("");
  const [expiryDate, setExpiryDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  // Pagination -----
  const [currPage, setCurrPage] = useState(1);
  const ITEMS_PER_PAGE = 8;

  const [allFilters, setAllFilters] = useState([]);
  const [filters, setFilters] = useState([]);
  const [filteredFilters, setFilteredFilters] = useState([]);
  const [editingFilter, setEditingFilter] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editOptionModalOpen, setEditOptionModalOpen] = useState(false);
  const [searchStockModalOpen, setSearchStockModalOpen] = useState(false);
  const [viewTagNoModalOpen, setViewTagNoModalOpen] = useState(false);
  const [existingTagNumbers, setExistingTagNumbers] = useState([]); // Store selected tag numbers
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [filterToDelete, setFilterToDelete] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isExhibitionOrder, setIsExhibitionOrder] = useState(false);
  const [exhibitionName, setExhibitionName] = useState("");
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (view === "get") {
      fetchFilters();
    }
  }, [view]);

  const fetchFilters = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/fetchorder/filter`,
        {
          params: { store_id: mfrId },
        }
      );
      setAllFilters(response.data);
      setFilters(response.data);
    } catch (error) {
      showSnackbar("Error fetching filters!", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Pagination logic
  const paginatedFilters = useMemo(() => {
    const startIndex = (currPage - 1) * ITEMS_PER_PAGE;
    return filters.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [currPage, filters]);

  const totalPages = Math.ceil(filters.length / ITEMS_PER_PAGE);

  const handlePageChange = (_, value) => {
    setCurrPage(value);
  };

  // Filter submit handler
  const handleSubmit = async () => {
    if (!filterName.trim() || !expiryDate) {
      showSnackbar("Filter Name and Expiry Date are required!", "error");
      return;
    }

    try {
      const payload = {
        name: filterName,
        expiryDate,
        isExhibitionOrder,
        exhibitionName,
        mfrId,
      };
      await axios.post(`${API_BASE_URL}/api/fetchorder/createfilter`, payload);
      showSnackbar("Filter created successfully!", "success");
      setFilterName("");
      setExpiryDate(new Date().toISOString().split("T")[0]);
      fetchFilters();
    } catch (error) {
      showSnackbar("Error saving filter!", "error");
    }
  };

  const handleEditClick = (filter) => {
    setEditingFilter(filter);
    setEditOptionModalOpen(true);
  };

  const fetchExistingTagNumbers = (filter) => {
    if (!filter || !Array.isArray(filter.selectedStock)) {
      setExistingTagNumbers([]);
      return;
    }
    setExistingTagNumbers(filter.selectedStock);
  };

  const handleEditOption = (option) => {
    setEditOptionModalOpen(false);
    if (option === "importTagno") {
      setEditModalOpen(true);
    } else if (option === "searchStock") {
      setSearchStockModalOpen(true);
    } else if (option === "viewTagNo") {
      fetchExistingTagNumbers(editingFilter);
      setViewTagNoModalOpen(true);
    }
  };

  const handleDownloadPDF = async (filter) => {
    if (!filter.selectedStock || filter.selectedStock.length === 0) {
      showSnackbar("No stock available to generate PDF.", "error");
      return;
    }

    const doc = new jsPDF("p", "mm", "a4");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text(`Catalogue - ${filter.name}`, 14, 20);
    doc.setFontSize(12);
    doc.text(
      `Expiry Date: ${new Date(filter.expiryDate).toLocaleDateString()}`,
      14,
      30
    );
    doc.text("Stock Details", 14, 40);

    let stockDetails = await fetchStockDetails(filter.selectedStock);

    doc.autoTable({
      startY: 45,
      head: [
        [
          "#",
          "Tag No",
          "Item Type",
          "Design",
          "Metal Type",
          "Purity",
          "Gross Weight",
        ],
      ],
      body: stockDetails.map((stock, index) => [
        index + 1,
        stock.tagno,
        stock.itemtype_name,
        stock.design_name,
        stock.purity_name,
        stock.purity,
        stock.gross,
      ]),
      theme: "grid",
    });

    doc.save(`${filter.name}.pdf`);
  };

  const fetchStockDetails = async (tagNumbers) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/fetchorder/getStockByTags`,
        { tagNumbers }
      );
      return response.data.map((stock) => ({
        tagno: stock.tagno,
        itemtype_name: stock.itemtype_name,
        design_name: stock.design_name,
        purity_name: stock.purity_name,
        purity: stock.purity,
        gross: stock.gross,
        imageUrl: `${IMAGE_BASE_URL}${stock.tagno}.jpg`,
      }));
    } catch (error) {
      return [];
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleDeleteClick = (filter) => {
    setFilterToDelete(filter);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteFilter = async () => {
    if (!filterToDelete) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/api/fetchorder/deletefilter/${filterToDelete.filterId}`,
        {
          params: { store_id: mfrId }, // send store_id as query param
        }
      );
      showSnackbar("Filter deleted successfully!", "success");
      fetchFilters();
    } catch (error) {
      showSnackbar("Error deleting filter!", "error");
    }
    setDeleteConfirmOpen(false);
    setFilterToDelete(null);
  };

  const handleViewInfo = (filter) => {
    navigate(`/filterinfo/${filter.filterId}`); // ✅ Pass filter data to new page
  };

  const [filterAllFiltersForm, setFilterAllFiltersForm] = useState({
    filterFormName: "",
    filterFormDate: "",
  });

  const filterAll = (name, date) => {
    let filtersToBeFiltered = [...allFilters];

    if (name) {
      filtersToBeFiltered = filtersToBeFiltered.filter((f) =>
        f.name.toLowerCase().includes(name.toLowerCase())
      );
    }

    if (date) {
      filtersToBeFiltered = filtersToBeFiltered.filter((f) => {
        const apiDate = new Date(f.createdAt).toISOString().split("T")[0];
        return apiDate === date;
      });
    }

    setFilters(filtersToBeFiltered);
    setCurrPage(1);
    setIsFilterActive(name || date ? true : false);

    if (!name && !date) fetchFilters();
  };

  const handleChange = (target, value) => {
    const newForm = {
      ...filterAllFiltersForm,
      [target]: value,
      ...(target === "filterFormName"
        ? { filterFormDate: "" }
        : { filterFormName: "" }), // ensure mutual exclusivity
    };

    setFilterAllFiltersForm(newForm);

    filterAll(newForm.filterFormName, newForm.filterFormDate);
  };

  const handleClearFilters = () => {
    setFilterAllFiltersForm({
      filterFormName: "",
      filterFormDate: "",
    });

    setIsFilterActive(false);
    setFilters(allFilters); // reset from master
    setCurrPage(1);
  };

  return (
    <>
      {/* Filter stock data */}
      <Paper
        elevation={1}
        sx={{ boxShadow: 0, p: 1.5, borderRadius: 2, mb: 1, px: 2 }}
      >
        <Box sx={{ width: "100%" }}>
          <Grid
            container
            spacing={2}
            justifyContent="center"
            textAlign="center"
          >
            {/* Filter by Name */}
            <Grid item xs={12} md="auto">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, whiteSpace: "nowrap" }}
                >
                  Search Filter by Name
                </Typography>
                <TextField
                  label="Enter filter name"
                  type="text"
                  size="small"
                  value={filterAllFiltersForm.filterFormName}
                  onChange={(e) =>
                    handleChange("filterFormName", e.target.value)
                  }
                  disabled={Boolean(filterAllFiltersForm.filterFormDate)}
                />
              </Stack>
            </Grid>

            {/* Filter by Date */}
            <Grid item xs={12} md="auto">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, whiteSpace: "nowrap" }}
                >
                  Search Filter by Created Date
                </Typography>
                <TextField
                  type="date"
                  size="small"
                  value={filterAllFiltersForm.filterFormDate}
                  onChange={(e) =>
                    handleChange("filterFormDate", e.target.value)
                  }
                  disabled={Boolean(filterAllFiltersForm.filterFormName)}
                />
              </Stack>
            </Grid>

            {/* Filter / Clear buttons */}
            <Grid item xs={12} md="auto">
              <Stack direction="row" spacing={1}>
                <Box sx={{ minWidth: 100 }}>
                  <Collapse in={isFilterActive} orientation="horizontal">
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={handleClearFilters}
                      sx={{ textTransform: "none", px: 3, height: 40 }}
                    >
                      Clear
                    </Button>
                  </Collapse>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" my={2}>
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(event, newView) => setView(newView)}
          >
            <ToggleButton value="get">Get Filters</ToggleButton>
            {}
            {isLoggedInUser?.role.toLowerCase !== "order" &&
              isLoggedInUser?.role.toLowerCase !== "digital order" && (
                <ToggleButton value="create">Create Filter</ToggleButton>
              )}
          </ToggleButtonGroup>
        </Box>

        {view === "create" && (
          <Box
            maxWidth="sm"
            margin="0 auto"
            mt={5}
            p={3}
            boxShadow={3}
            borderRadius={2}
          >
            <Typography variant="h5" textAlign="center" sx={{ mb: 2 }}>
              Create a Filter
            </Typography>
            <TextField
              fullWidth
              label="Filter Name"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Expiry Date"
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isExhibitionOrder}
                  onChange={(e) => setIsExhibitionOrder(e.target.checked)}
                />
              }
              label="Is Exhibition Order?"
            />
            {isExhibitionOrder && (
              <TextField
                fullWidth
                label="Exhibition Name"
                value={exhibitionName}
                onChange={(e) => setExhibitionName(e.target.value)}
                sx={{ mb: 2 }}
              />
            )}
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSubmit}
            >
              Create
            </Button>
          </Box>
        )}

        {view === "get" && (
          <>
            <Grid container spacing={2}>
              {!isLoading && paginatedFilters.length === 0 && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      width: "100%",
                      minHeight: "150px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Typography>
                      No filters are available with this name/date
                    </Typography>
                  </Box>
                </Grid>
              )}

              {paginatedFilters.map((filter) => (
                <Grid item sm={12} md={6} key={filter.filterId}>
                  <Box
                    sx={{
                      width: "100%",
                      px: { xs: 2, sm: 3 },
                      py: { xs: 1.5, sm: 2 },
                      borderRadius: 3,
                      boxShadow: 2,
                      bgcolor: "background.paper",
                      transition: "all 0.3s",
                      "&:hover": {
                        boxShadow: 4,
                        transform: "translateY(-2px)",
                        bgcolor: "#f9f9f9",
                      },
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    {/* Name and Expiry Row */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                      }}
                    >
                      {/* Filter name */}
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: "primary.main",
                          fontWeight: 600,
                          fontSize: "1rem",
                        }}
                      >
                        {filter.name}
                      </Typography>

                      {/* Creation and Validity */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "flex-end",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        {/* Creation */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#2c2c2cff",
                            fontSize: "0.8rem",
                            fontWeight: 500,
                          }}
                        >
                          Created:{" "}
                          {new Date(filter.createdAt).toLocaleDateString(
                            "en-GB",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            }
                          )}
                        </Typography>

                        {/* Validity */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#2c2c2cff",
                            fontSize: "0.8rem",
                            fontWeight: 500,
                          }}
                        >
                          Expires:{" "}
                          <span style={{ color: "#d32222ff" }}>
                            {new Date(filter.expiryDate).toLocaleDateString(
                              "en-GB",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              }
                            )}
                          </span>
                        </Typography>
                      </Box>
                    </Box>

                    {/* Icons Row */}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "flex-start",
                        gap: 1,
                      }}
                    >
                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() => handleEditClick(filter)}
                          color="default"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download PDF">
                        <IconButton
                          onClick={() => handleDownloadPDF(filter)}
                          color="default"
                        >
                          <PictureAsPdf fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy Link">
                        <IconButton
                          onClick={() => {
                            navigator.clipboard.writeText(
                              `${FRONTEND_URL}/getstockdata/${filter.filterId}`
                            );
                            showSnackbar(
                              "Link copied to clipboard!",
                              "success"
                            );
                          }}
                          color="default"
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => handleDeleteClick(filter)}
                          color="default"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Info">
                        <IconButton
                          onClick={() => handleViewInfo(filter)}
                          color="default"
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Box
              sx={{
                backgroundColor: "#fff",
                p: { xs: 1.5, sm: 2 },
                pb: { xs: "8rem", sm: "8rem" },
                borderRadius: { xs: 2, sm: "12px" },
                display: "flex",
                justifyContent: "center",
                zIndex: 10,
                mx: { xs: 1, sm: 0 },
              }}
            >
              <Pagination
                count={totalPages}
                page={currPage}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                size="medium"
                siblingCount={1}
                boundaryCount={1}
              />
            </Box>
          </>
        )}

        {/* Edit‑option chooser modal */}
        <Dialog
          open={editOptionModalOpen}
          onClose={() => setEditOptionModalOpen(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle sx={{ textAlign: "center" }}>
            Select an Option
          </DialogTitle>
          <DialogActions
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "stretch",
              justifyContent: "center",
              gap: 2,
              px: 3,
              pb: 3,
            }}
          >
            <Box
              sx={{
                display: { xs: "block", sm: "flex" },
                flexDirection: "row",
                gap: 2,
                my: 1,
              }}
            >
              <Button
                onClick={() => handleEditOption("importTagno")}
                variant="contained"
                color="primary"
                fullWidth
                sx={{ my: { xs: 1, sm: 0 }, flex: 1, minWidth: 180 }}
              >
                Import TagNo
              </Button>
              <Button
                onClick={() => handleEditOption("searchStock")}
                variant="outlined"
                color="primary"
                fullWidth
                sx={{ my: { xs: 1, sm: 0 }, flex: 1, minWidth: 180 }}
              >
                Search Stock
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Delete‑confirmation modal */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleDeleteFilter} color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit filter */}
        {editingFilter && (
          <EditFilter
            open={editModalOpen}
            onClose={() => setEditModalOpen(false)}
            filter={editingFilter}
            onUpdate={fetchFilters}
          />
        )}

        {/* Search stock */}
        {editingFilter && (
          <SearchStock
            open={searchStockModalOpen}
            onClose={() => setSearchStockModalOpen(false)}
            filter={editingFilter}
          />
        )}

        {/* Snackbar notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          sx={{
            "&.MuiSnackbar-root": {
              bottom: "100px !important", // force override snackbar position
            },
          }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
            variant="filled"
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default FilterForm;
